import { environment } from "@app/relay/relay-environment";
import { useRouter } from "@tanstack/react-router";
import { useState } from "react";
import { commitMutation, graphql, usePreloadedQuery } from "react-relay";
import type { PreloadedQuery } from "react-relay";

import type { statementsPage_Query } from "@app/queries/statementsPage_Query.graphql";
import type { statementsPage_CreateExpectedSourceMutation } from "@app/queries/statementsPage_CreateExpectedSourceMutation.graphql";
import type { statementsPage_DeleteExpectedSourceMutation } from "@app/queries/statementsPage_DeleteExpectedSourceMutation.graphql";

import UploadZone from "./upload-zone";
import AccountDropzone from "./account-dropzone";
import type { DropzoneAccount } from "./account-dropzone";

export const statementsQuery = graphql`
  query statementsPage_Query {
    accountsRegistry {
      accounts {
        number
        nickname
        kind
        bank
        providers
        txnCount
        firstMonth
        lastMonth
        coveredMonths
        gapMonths
        staleSince
        files {
          filename
          provider
          startDate
          endDate
          state
          txnCount
          uploadedAt
        }
        monthly {
          month
          cashIn
          cashOut
          net
          balance
        }
      }
      expectedSources {
        id
        name
        note
      }
    }
  }
`;

const createExpectedSourceMutation = graphql`
  mutation statementsPage_CreateExpectedSourceMutation(
    $input: CreateExpectedSourceInputObject!
  ) {
    createExpectedSource(input: $input) {
      expectedSource {
        id
      }
      errors {
        message
      }
    }
  }
`;

const deleteExpectedSourceMutation = graphql`
  mutation statementsPage_DeleteExpectedSourceMutation(
    $input: DeleteExpectedSourceInputObject!
  ) {
    deleteExpectedSource(input: $input) {
      expectedSource {
        id
      }
      errors {
        message
      }
    }
  }
`;

type Account =
  statementsPage_Query["response"]["accountsRegistry"]["accounts"][number];

const KIND_LABELS: Record<string, string> = {
  BANK_ACCOUNT: "Bank account",
  CARD: "Card",
  INVOICE_LEDGER: "Invoice ledger",
};

function institutionOf(account: Account): string {
  const provider = account.providers[0];
  if (provider?.startsWith("CHASE")) return "Chase";
  if (provider === "WISE") return "Wise";
  if (provider === "DEEL") return "Deel";
  return account.bank ?? "Other";
}

const money = (value: number) =>
  value.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });

function AccountRow({
  account,
  accounts,
}: {
  account: Account;
  accounts: DropzoneAccount[];
}) {
  const [expanded, setExpanded] = useState<"monthly" | "files" | null>(null);

  const toggle = (tab: "monthly" | "files") =>
    setExpanded(expanded === tab ? null : tab);
  const monthlyDesc = [...account.monthly].reverse();

  return (
    <div className="border-b last:border-b-0">
      <div className="px-4 py-3 grid grid-cols-12 gap-2 items-center text-sm">
        <div className="col-span-3">
          <span className="font-medium">
            {account.nickname ?? account.number}
          </span>
          <span className="ml-2 text-xs text-gray-400">
            {KIND_LABELS[account.kind] ?? account.kind}
          </span>
        </div>
        <div className="col-span-2 text-xs text-gray-500">
          {account.providers.join(", ")}
        </div>
        <div className="col-span-2 text-right">
          {account.txnCount.toLocaleString()} txns
        </div>
        <div className="col-span-2 text-xs text-gray-500 text-right">
          {account.firstMonth} → {account.lastMonth}
        </div>
        <div className="col-span-1 text-right">
          {account.gapMonths.length > 0 ? (
            <span className="text-amber-600 text-xs font-medium">
              {account.gapMonths.length} gaps
            </span>
          ) : (
            <span className="text-green-600 text-xs">complete</span>
          )}
        </div>
        <div className="col-span-2 flex justify-end gap-1">
          {account.staleSince && (
            <span className="text-xs bg-red-50 text-red-700 rounded px-1.5 py-0.5">
              stale since {account.staleSince}
            </span>
          )}
          <button
            type="button"
            onClick={() => toggle("monthly")}
            className={`text-xs rounded border px-2 py-0.5 ${
              expanded === "monthly"
                ? "bg-gray-900 text-white"
                : "text-gray-600"
            }`}
          >
            Monthly
          </button>
          <button
            type="button"
            onClick={() => toggle("files")}
            className={`text-xs rounded border px-2 py-0.5 ${
              expanded === "files" ? "bg-gray-900 text-white" : "text-gray-600"
            }`}
          >
            Files ({account.files.length})
          </button>
        </div>
      </div>

      <AccountDropzone
        account={{
          number: account.number,
          nickname: account.nickname ?? null,
          kind: account.kind,
        }}
        accounts={accounts}
      />

      {expanded === "monthly" && (
        <div className="px-4 pb-3 max-h-80 overflow-y-auto">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-white">
              <tr className="text-left text-gray-400 uppercase border-b">
                <th className="py-1.5">Month</th>
                <th className="py-1.5 text-right">Money in</th>
                <th className="py-1.5 text-right">Money out</th>
                <th className="py-1.5 text-right">Net</th>
                <th className="py-1.5 text-right">Statement balance</th>
              </tr>
            </thead>
            <tbody>
              {monthlyDesc.map((row) => (
                <tr
                  key={row.month}
                  className={`border-b last:border-b-0 ${
                    account.gapMonths.includes(row.month) ? "bg-amber-50" : ""
                  }`}
                >
                  <td className="py-1">{row.month}</td>
                  <td className="py-1 text-right text-green-700">
                    {money(row.cashIn)}
                  </td>
                  <td className="py-1 text-right text-red-700">
                    {money(row.cashOut)}
                  </td>
                  <td className="py-1 text-right font-medium">
                    {money(row.net)}
                  </td>
                  <td className="py-1 text-right text-gray-500">
                    {row.balance == null ? "—" : money(row.balance)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="text-[11px] text-gray-400 mt-1.5">
            Raw statement basis — internal transfers included, to match the
            bank's own export. Amber rows are coverage gaps.
          </p>
        </div>
      )}

      {expanded === "files" && (
        <div className="px-4 pb-3">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left text-gray-400 uppercase border-b">
                <th className="py-1.5">File</th>
                <th className="py-1.5">Provider</th>
                <th className="py-1.5">Covers</th>
                <th className="py-1.5 text-right">Rows</th>
                <th className="py-1.5 text-right">State</th>
              </tr>
            </thead>
            <tbody>
              {account.files.map((file, i) => (
                <tr key={i} className="border-b last:border-b-0">
                  <td className="py-1 font-mono">{file.filename ?? "?"}</td>
                  <td className="py-1 text-gray-500">{file.provider}</td>
                  <td className="py-1 text-gray-500">
                    {file.startDate} → {file.endDate}
                  </td>
                  <td className="py-1 text-right">
                    {file.txnCount.toLocaleString()}
                  </td>
                  <td className="py-1 text-right">
                    <span
                      className={
                        file.state === "SUCCESS"
                          ? "text-green-600"
                          : "text-amber-600"
                      }
                    >
                      {file.state}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function ExpectedSources({
  sources,
}: {
  sources: statementsPage_Query["response"]["accountsRegistry"]["expectedSources"];
}) {
  const router = useRouter();
  const [name, setName] = useState("");
  const [note, setNote] = useState("");

  const add = () => {
    if (!name.trim()) return;

    commitMutation<statementsPage_CreateExpectedSourceMutation>(environment, {
      mutation: createExpectedSourceMutation,
      variables: { input: { name: name.trim(), note: note.trim() || null } },
      onCompleted: () => {
        setName("");
        setNote("");
        router.invalidate();
      },
    });
  };

  const remove = (expectedSourceId: string) => {
    commitMutation<statementsPage_DeleteExpectedSourceMutation>(environment, {
      mutation: deleteExpectedSourceMutation,
      variables: { input: { expectedSourceId } },
      onCompleted: () => router.invalidate(),
    });
  };

  return (
    <section className="space-y-2">
      <div>
        <h2 className="font-bold">Expected sources</h2>
        <p className="text-xs text-gray-400">
          Entities money flows through that have no data here yet (e.g.
          Rippling). Listed so absence is visible — remove an entry once its
          statements are imported.
        </p>
      </div>

      {sources.length > 0 && (
        <ul className="bg-white rounded border divide-y text-sm">
          {sources.map((source) => (
            <li
              key={source.id}
              className="px-4 py-2.5 flex items-center justify-between"
            >
              <div>
                <span className="font-medium">{source.name}</span>
                {source.note && (
                  <span className="ml-2 text-gray-400 text-xs">
                    {source.note}
                  </span>
                )}
                <span className="ml-3 text-xs bg-red-50 text-red-700 rounded px-1.5 py-0.5">
                  no data yet
                </span>
              </div>
              <button
                type="button"
                onClick={() => remove(source.id)}
                className="text-gray-300 hover:text-red-600"
                aria-label={`Remove ${source.name}`}
              >
                ✕
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="flex gap-2">
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Source name, e.g. Rippling"
          className="rounded border px-3 py-1.5 text-sm w-56"
        />
        <input
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="What flows through it (optional)"
          className="rounded border px-3 py-1.5 text-sm w-72"
        />
        <button
          type="button"
          onClick={add}
          disabled={!name.trim()}
          className="rounded border px-3 py-1.5 text-sm font-medium text-gray-700 disabled:opacity-40"
        >
          Add expected source
        </button>
      </div>
    </section>
  );
}

export default function StatementsPage({
  queryRef,
}: {
  queryRef: PreloadedQuery<statementsPage_Query>;
}) {
  const data = usePreloadedQuery(statementsQuery, queryRef);
  const { accounts, expectedSources } = data.accountsRegistry;

  const dropzoneAccounts: DropzoneAccount[] = accounts.map((a) => ({
    number: a.number,
    nickname: a.nickname ?? null,
    kind: a.kind,
  }));

  const groups = new Map<string, Account[]>();
  for (const account of accounts) {
    const institution = institutionOf(account);
    groups.set(institution, [...(groups.get(institution) ?? []), account]);
  }

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6">
      <div>
        <h1 className="text-xl font-bold">Statements & sources</h1>
        <p className="text-xs text-gray-400">
          Every account data flows through, the files behind it, and a
          month-by-month gut-check against the statements themselves.
        </p>
      </div>

      <UploadZone />

      {[...groups.entries()].map(([institution, group]) => (
        <section key={institution} className="space-y-2">
          <h2 className="font-bold">{institution}</h2>
          <div className="bg-white rounded border">
            {group.map((account) => (
              <AccountRow
                key={account.number}
                account={account}
                accounts={dropzoneAccounts}
              />
            ))}
          </div>
        </section>
      ))}

      <ExpectedSources sources={expectedSources} />
    </div>
  );
}
